/**
 * useFields Composable
 *
 * The fields of a record type: its fixed columns, its type schema, every other
 * schema of the type, and the relation fields. Sourced from the preloaded
 * types and schemas stores — reactive, no fetch.
 *
 * @example
 * const { fields, schemaNames } = useFields(() => "person")
 *
 * // Rows that wrap their record (a deal listing): schema fields read the
 * // record's data through `data`.
 * const { typeSchemaFields } = useFields(type, { data: (row) => row?.record?.data })
 */

import { computed, type MaybeRefOrGetter, toValue } from "vue"
import type { SchemaField } from "@auger/core"
import { enrichField, recordFixedFields } from "@auger/core/collections"
import { type AugerField, getListParentFields, getRelationshipFields } from "@auger/core/fields"
import { useSchemasStore } from "@/stores/schemas"
import { getType } from "@/stores/types"

export interface UseFieldsOptions {
  // Where a row keeps the record's jsonb data; defaults to the row's own `data`.
  data?: (item: unknown) => Record<string, unknown> | null | undefined
}

export function useFields(
  recordTypeName: MaybeRefOrGetter<string | null | undefined>,
  options: UseFieldsOptions = {}
) {
  const schemasStore = useSchemasStore()

  const typeName = computed(() => toValue(recordTypeName) || null)
  const recordType = computed(() => (typeName.value ? getType(typeName.value) : null))
  // The schemas of this type (not the untyped ones).
  const schemas = computed(() => (typeName.value ? schemasStore.forType(typeName.value) : []))

  const schemaNames = computed(
    () => new Map(schemas.value.filter((s) => s.id && s.name).map((s) => [s.id, s.name] as const))
  )

  const { data } = options
  const schemaField = (f: SchemaField, schemaId: number | undefined) => {
    if (!data) return enrichField(f, { schema_id: schemaId, isEditable: true })
    const read = (item: unknown) => data(item)?.[f.key]
    return enrichField(f, { schema_id: schemaId, isEditable: true, value: read, rawValue: read })
  }

  // The record's own columns, enriched so reference fields (owner, schema, …)
  // resolve their ids.
  const fixedFields = computed(() =>
    recordFixedFields.map((f) => enrichField(f, { value: f.value }))
  )

  // Inherited from the record type.
  const typeSchemaFields = computed(() => {
    const schema = recordType.value?.schema
    return (schema?.fields ?? []).map((f) => schemaField(f, schema?.id))
  })

  // Every other schema of the type, one field per schema and key.
  const recordSchemaFields = computed(() => {
    const typeSchemaId = recordType.value?.schema?.id
    const seen = new Set<string>()
    const fields: AugerField[] = []
    for (const schema of schemas.value) {
      if (schema.id === typeSchemaId || !Array.isArray(schema.fields)) continue
      for (const f of schema.fields) {
        const uniqueKey = `${schema.id}:${f.key}`
        if (seen.has(uniqueKey)) continue
        seen.add(uniqueKey)
        fields.push(schemaField(f, schema.id))
      }
    }
    return fields
  })

  // On-record relations: tags, contacts, times, attachments, sources, links.
  const relationshipFields = getRelationshipFields()

  // Events / Organizations / People: records joined through a list, computed
  // in Postgres (records_events, …). Only where the query selects them.
  const listRelationFields = getListParentFields()

  const fields = computed<AugerField[]>(() => [
    ...fixedFields.value,
    ...typeSchemaFields.value,
    ...recordSchemaFields.value,
    ...relationshipFields,
  ])

  return {
    recordType,
    schemas,
    schemaNames,
    fixedFields,
    typeSchemaFields,
    recordSchemaFields,
    relationshipFields,
    listRelationFields,
    fields,
  }
}
